/**
 * Scroll Reveal - Fade effect when elements appear on scroll
 * Uses native Intersection Observer API
 * Automatically handles dynamically loaded content (AJAX)
 */

class ScrollReveal {
  constructor() {
    /** @type {IntersectionObserver | null} */
    this.observer = null;

    /** @type {MutationObserver | null} */
    this.mutationObserver = null;

    /** @type {WeakSet<Element>} */
    this.observedElements = new WeakSet();

    /** @type {{ root: Element | null, rootMargin: string, threshold: number } | null} */
    this.options = null;
    this.init();
  }

  init() {
    const options = {
      root: null,
      rootMargin: '0px 0px -100px 0px', // Trigger 100px before element enters viewport
      threshold: 0.1, // Trigger when 10% of element is visible
    };

    this.options = options;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!(entry.target instanceof HTMLElement)) return;

        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          
          // Optional: Stop observing after reveal (one-time animation)
          // add data-scroll-reveal-once="false" to allow re-animation
          const once = entry.target.dataset.scrollRevealOnce !== 'false';
          if (once) {
            this.observer?.unobserve(entry.target);
          }
        } else {
          // If data-scroll-reveal-once="false", allow re-animation
          const once = entry.target.dataset.scrollRevealOnce !== 'false';
          if (!once) {
            entry.target.classList.remove('is-revealed');
          }
        }
      });
    }, options);

    // Observe existing elements
    this.observeElements();

    // Watch for new elements added to the DOM (for AJAX content)
    this.setupMutationObserver();
  }

  /**
   * Force a synchronous visibility check.
   * Useful after large DOM morphs where IntersectionObserver callbacks may not
   * run before paint, leaving elements invisible until the next scroll.
   */
  check() {
    const elements = document.querySelectorAll('[data-scroll-reveal]');
    const { topMargin, bottomMargin } = this.parseRootMargin();
    const threshold = typeof this.options?.threshold === 'number' ? this.options.threshold : 0;

    const viewportTop = 0 - topMargin;
    const viewportBottom = window.innerHeight + bottomMargin;

    elements.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const rect = element.getBoundingClientRect();
      const elementHeight = rect.height || 1;

      const intersectionTop = Math.max(rect.top, viewportTop);
      const intersectionBottom = Math.min(rect.bottom, viewportBottom);
      const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);
      const ratio = intersectionHeight / elementHeight;

      const isVisible = ratio >= threshold && intersectionHeight > 0;
      const once = element.dataset.scrollRevealOnce !== 'false';

      if (isVisible) {
        element.classList.add('is-revealed');
        if (once && this.observer) {
          this.observer.unobserve(element);
        }
      } else if (!once) {
        element.classList.remove('is-revealed');
      }
    });
  }

  parseRootMargin() {
    const rootMargin = this.options?.rootMargin || '0px 0px 0px 0px';
    const parts = rootMargin.trim().split(/\s+/);
    const first = parts[0] ?? '0px';
    const second = parts[1] ?? first;
    const third = parts[2] ?? first;
    const fourth = parts[3] ?? second;

    /** @param {string} value */
    const toPx = (value) => {
      const match = String(value).match(/^(-?\d+(?:\.\d+)?)px$/);
      return match && match[1] ? parseFloat(match[1]) : 0;
    };

    const top = toPx(first);
    const bottom = toPx(third);
    return { topMargin: top, bottomMargin: bottom };
  }

  observeElements() {
    const observer = this.observer;
    if (!observer) return;
    const elements = document.querySelectorAll('[data-scroll-reveal]');
    elements.forEach((element) => {
      if (!this.observedElements.has(element)) {
        observer.observe(element);
        this.observedElements.add(element);
      }
    });
  }

  setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check added nodes for data-scroll-reveal attribute
        mutation.addedNodes.forEach((node) => {
          const observer = this.observer;
          if (!observer) return;
          if (!(node instanceof Element)) return;

          // Check if the node itself has the attribute
          if (node.hasAttribute('data-scroll-reveal')) {
            if (!this.observedElements.has(node)) {
              observer.observe(node);
              this.observedElements.add(node);
            }
          }

          // Check for children with the attribute
          const children = node.querySelectorAll('[data-scroll-reveal]');
          children.forEach((child) => {
            if (!this.observedElements.has(child)) {
              observer.observe(child);
              this.observedElements.add(child);
            }
          });
        });
      });
    });

    // Start observing the document for changes
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  // Public method to manually trigger observation of new elements
  refresh() {
    this.observeElements();
    this.check();
  }
}

// Create singleton instance
/** @type {ScrollReveal | null} */
let scrollRevealInstance = null;

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    scrollRevealInstance = new ScrollReveal();
  });
} else {
  scrollRevealInstance = new ScrollReveal();
}

// Re-initialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', () => {
  if (scrollRevealInstance) {
    scrollRevealInstance.refresh();
  }
});

// Export for manual refresh if needed
window.ScrollReveal = {
  refresh: () => scrollRevealInstance?.refresh(),
  check: () => scrollRevealInstance?.check(),
};
